<template>
  <tabs class="!py-5" />
  <toolbar class="toolbar">
    <filter-pane v-model:page="page" v-model="filterParams" :filters="filters" />
    <add-fields-pane v-model="visibleColumns" :fields="columns" />
    <pane
      id="table-export"
      :title="$t('toolbar.export.title')"
      :label="$t('toolbar.export.label')"
      :icon="ExportIcon"
    >
      <ExportLink
        :url="`/licenses/${license?.id}/reporting_xlsx`"
        :filter="providerParams.filter"
        :columns="columns"
        :visible-columns="visibleColumns"
        :title="csvTitle"
        :sort-key="sortKey"
        :sort-direction="sortDirection"
        :label="$t('toolbar.export.downloadXlsxLabel')"
      />
    </pane>
  </toolbar>
  <div v-if="!globalFilter?.licenseId">
    <div class="ml-12 py-8 text-red-80">
      {{ t('pages.companyReporting.noLicenseSelected') }}
    </div>
  </div>
  <div v-else>
    <div class="ml-12 py-8 md:w-1/2">
      <DateRange
        v-model="reportingRange"
        mode="date"
        :label="$t('pages.companyReporting.fields.reportingRange')"
      />
    </div>
    <div class="ml-12 flex flex-wrap gap-x-20 gap-y-10">
      <ul class="grid grid-cols-[repeat(2,max-content)] gap-x-2 gap-y-3 w-fit pt-5 text-body-regular" v-show="!licenseLoading">
        <li class="text-body-regular-bold text-table-text-gray uppercase col-span-2">
          {{ $t('pages.companyReporting.statisticsHeaderDetails') }}
        </li>
        <li class="contents">
          <span class="text-body-regular-bold justify-self-end">{{t('pages.companyReporting.fields.companyName')}}</span>
          <span>{{ props.modelValue?.name }}</span>
        </li>
        <li class="contents">
          <span class="text-body-regular-bold justify-self-end">{{t('pages.companyReporting.fields.companyAddress')}}</span>
          <p class="inline-block">
            {{ props.modelValue?.contactAddress?.street }}<br />
            {{ props.modelValue?.contactAddress?.zip }} {{ props.modelValue?.contactAddress?.cityName }}
          </p>
        </li>
        <li class="contents">
          <span class="text-body-regular-bold justify-self-end">{{t('pages.companyReporting.fields.licenseRange')}}</span>
          <span>
            {{license?.startDate ? format(new Date(license.startDate), 'dd.MM.yyyy') : ''}}
            -
            {{license?.endDate ? format(new Date(license.endDate), 'dd.MM.yyyy') : ''}}
          </span>
        </li>
        <li class="contents">
          <span class="text-body-regular-bold justify-self-end">{{t('pages.companyReporting.fields.licenseGoal')}}</span>
          <span>
            {{formatLicenseGoal(license)}}
          </span>
        </li>
        <li class="contents">
          <span class="text-body-regular-bold justify-self-end">{{t('pages.companyReporting.fields.licenseReward')}}</span>
          <span>
            {{formatLicenseReward(license)}}
          </span>
        </li>
        <li class="contents">
          <span class="text-body-regular-bold justify-self-end w-min hyphens-none text-right">{{t('pages.companyReporting.fields.licenseProgress')}}</span>
          <span>
            {{filteredStatistics?.licenseProgress.toFixed(2)}}%
          </span>
        </li>
        <li class="contents">
          <span class="text-body-regular-bold justify-self-end">{{t('pages.companyReporting.fields.numberOfDrivers')}}</span>
          <span>
            {{items?.['hydra:totalItems']}}
          </span>
        </li>
      </ul>
      <CompanyPayoutStatisticsTable
        :statistics="{
          total: filteredStatistics?.filtered ?? {},
        }"
        :hide-filtered="true"
        :hide-selected="true"
        :loading="loadingStatistics"
        :admins-are-allowed="true"
      />
      <data-table
        class="mb-auto w-full"
        v-model:sort-by="sortKey"
        v-model:sort-direction="sortDirection"
        :busy="loading"
        :items="records"
        :columns="columns"
        :visible-columns="visibleColumns"
        :disableFrontendSorting="true"
      />
    </div>
  </div>
  <pagination v-model:page="page" v-model:per-page="perPage" class="pagination" :total="total" />
</template>

<script lang="tsx" setup>
import {computed, inject, reactive, ref, unref, watch} from 'vue';
import {useI18n} from 'vue-i18n';
import FilterPane from '@components/utils/toolbar/panes/Filter.vue';
import AddFieldsPane from '@components/utils/toolbar/panes/AddFields.vue';
import Toolbar from '@components/utils/toolbar/Toolbar.vue';
import Pagination from '@components/utils/table/Pagination.vue';
import DataTable, {type Column} from '@components/utils/table/Table.vue';
import {
    enumOptionsProvider,
    useProvider,
} from '@components/utils/table/hooks/provider';
import {format} from 'date-fns';
import DateRange from '@/components/utils/inputs/DateRange.vue';
import type {Filter, ProviderParams} from '@/components/utils/table/hooks/provider';
import type {Company, Driver, HydraCollection, License} from '@/entities/Entities';
import Tabs from '@components/structure/navigation/Tabs.vue';
import Pane from '@components/utils/toolbar/Pane.vue';
import ExportIcon from '@images/export.svg?component';
import ExportLink from '@utils/toolbar/panes/ExportLink.vue';
import {useQueryRef} from '@/hooks/url';
import {useTableFields} from '@hooks/table-configuration';
import { useRoute } from 'vue-router';
import globalLicenseFilterKey from '@/components/utils/globalLicenseFilter/injects';
import { StatisticsItem } from '@/hooks/company-payout';
import CompanyPayoutStatisticsTable from '@/components/utils/CompanyPayoutStatisticsTable.vue';
import { useFetchQuery } from '@/hooks/fetch';
import { formatTimeDurationFromSeconds } from '@/utils/helpers';

interface Props {
  modelValue: Company | null;
}

const props = defineProps<Props>();

const hideDeleted = ref(true);

const route = useRoute();

const page = useQueryRef('page', 1);
const perPage = useQueryRef('perPage', 10);
const filter = useQueryRef('filter', {});

const sortKey = useQueryRef<string | undefined>('sortKey', 'createdAt');
const sortDirection = useQueryRef<'ASC' | 'DESC' | undefined>('sortDirection', 'DESC');

const a = new Date();
a.setMonth(a.getMonth() - 3);
const reportingRange = ref<{start: Date, end: Date}>({start: a, end: new Date()});

const { t } = useI18n();

const globalFilter = inject(globalLicenseFilterKey)!
const {
  data: license,
  loading: licenseLoading,
  refetch: licenseRefetch,
} = useFetchQuery<License>(globalFilter.value?.licenseId ?? '/licenses/null');
watch(() => globalFilter.value?.licenseId, () => {
  if(!globalFilter.value?.licenseId) return;
  licenseRefetch(globalFilter.value.licenseId);
});
const csvTitle = computed(() => {
  let title = t('csv.title.reportingFromCompany', {
    company: props.modelValue?.name,
  });

  if(license.value) {
    title += `_${license.value?.startDate ? format(new Date(license.value.startDate), 'dd-MM-yyyy') : ''}_${license.value?.endDate ? format(new Date(license.value.endDate), 'dd-MM-yyyy') : ''}`;
  }

  if(globalFilter.value?.boxType) {
    title += `_${t('licenses.boxType.'+globalFilter.value.boxType)}`;
  }

  return title;
});


const sortParams = reactive({
  sortKey,
  sortDirection,
})
const filterParams = ref<Filter>(filter.value)
const paginationParams = reactive({
  page,
  perPage,
})
const providerParams = computed<ProviderParams>(() => {
  const filter: Filter = {
    ...filterParams.value,
    'licenseSeat.license.company': `/companies/${route.params.companyId}`,
  }

  if(globalFilter.value?.licenseId) {
    filter['licenseSeat.license'] = globalFilter.value.licenseId
  }

  if(globalFilter.value?.boxType) {
    filter['licenseSeat.type'] = globalFilter.value.boxType
  }

  if (reportingRange.value) {
    const start = new Date(reportingRange.value.start.toISOString());
    start.setHours(0, 0, 0, 0);
    const end = new Date(reportingRange.value.end.toISOString());
    end.setHours(23, 59, 59, 999);
    filter['startDate'] = start.toISOString();
    filter['endDate'] = end.toISOString();
  }

  if (hideDeleted.value) {
    filter['driver.user.deleted'] = false
  }
  return {
    sort: sortParams,
    filter,
    pagination: paginationParams,
  }
});
const licenseStatisticsParams = computed<ProviderParams>(() => {
  const filter: Filter = {
    ...filterParams.value,
  }

  if(globalFilter.value?.licenseId) {
    filter['id'] = globalFilter.value.licenseId
  }

  if (reportingRange.value) {
    const start = new Date(reportingRange.value.start.toISOString());
    start.setHours(0, 0, 0, 0);
    const end = new Date(reportingRange.value.end.toISOString());
    end.setHours(23, 59, 59, 999);
    filter['startDate'] = start.toISOString();
    filter['endDate'] = end.toISOString();
  }

  return {
    filter,
  }
});

type ColumnFilter = Partial<Filter> & Omit<Filter, 'key'>;
type ColumnDefinition = Column & { filter?: ColumnFilter | ColumnFilter[] } & { group: string };

const columns: ColumnDefinition[] = [
  {
    key: 'id',
    title: t('id'),
    group: t('pages.driverList.fieldGroups.general'),
    class: 'whitespace-nowrap',
    sortable: true,
  },
  {
    key: 'user.salutation',
    csvTranslations: {
      male: t('title.male'),
      female: t('title.female'),
      other: t('title.other'),
      unknown: t('title.unknown'),
    },
    title: t('driver.title'),
    group: t('pages.driverList.fieldGroups.general'),
    formatter: (v: string) => t(`title.${v ?? 'unknown'}`),
    filter: {
      type: 'dropdown',
      group: t('pages.driverList.filterGroups.general'),
      options: {
        options: enumOptionsProvider('/enums/salutation', (v) => ({
          label: t(`title.${v}`),
          value: v,
        })),
        label: t('driver.title'),
      },
    },
  },
  {
    key: 'user.firstName',
    title: t('user.firstName'),
    sortable: true,
    visible: true,
    group: t('pages.driverList.fieldGroups.general'),
    filter: {
      type: 'text',
      group: t('pages.driverList.filterGroups.general'),
      options: {
        label: t('user.firstName'),
      },
    },
  },
  {
    key: 'user.lastName',
    title: t('user.lastName'),
    group: t('pages.driverList.fieldGroups.general'),
    sortable: true,
    visible: true,
    filter: {
      type: 'text',
      group: t('pages.driverList.filterGroups.general'),
      options: {
        label: t('user.lastName'),
      },
    },
  },
  {
    key: 'totalDrivenDistance',
    title: t('driver.totalDistance'),
    group: t('pages.driverList.fieldGroups.calculations'),
    sortable: false,
    visible: true,
    unit: 'km',
    formatter: (v: number) => (v / 1000).toFixed(2) + ' km',
    /* WOR-2166 @Oguz Han
    filter: {
      type: 'range',
      group: t('pages.driverList.filterGroups.calculations'),
      options: {
        icon: <span>km</span>,
        label: t('driver.totalDistance'),
        transformOut: (v: number) => v * 1000,
        transformIn: (v: number) => v / 1000,
      },
    },*/
  },
  {
    key: 'totalDrivenTime',
    title: t('driver.totalTime') + ' (HH:MM:SS)',
    group: t('pages.driverList.fieldGroups.calculations'),
    sortable: false,
    formatter: (v: number) => `${formatTimeDurationFromSeconds(v)}`,
    /* WOR-2166 @Oguz Han
    filter: {
      type: 'duration',
      group: t('pages.driverList.filterGroups.calculations'),
      options: {
        label: t('driver.totalTime'),
        placeholderFrom: 'hh:mm:ss',
        placeholderTo: 'hh:mm:ss',
      },
    } */
  },
  {
    key: 'numOfDaysWithTour',
    title: t('driver.numOfDaysWithTour'),
    group: t('pages.driverList.fieldGroups.calculations'),
    sortable: false,
    /* WOR-2166 @Oguz Han
    filter: {
      type: 'range',
      group: t('pages.driverList.filterGroups.calculations'),
      options: {
        label: t('driver.numOfDaysWithTour'),
      },
    }, */
  },
  {
    key: 'licenseProgress',
    title: t('driver.licenseProgress'),
    group: t('pages.driverList.fieldGroups.calculations'),
    sortable: false,
    formatter: (v: number) => `${v.toFixed(2).padStart(5, '0')}%`,
  },
];

const visibleColumns = useTableFields(
    columns,
);

export interface Statistics {
  filtered: StatisticsItem;
  total: StatisticsItem;
  licenseProgress: number;
}

const {
  data: filteredStatistics,
  loading: loadingStatistics,
} = useProvider<Statistics>('/licenses/payout-statistics', licenseStatisticsParams);

const { data: items, loading } = useProvider<HydraCollection<Driver>>('/drivers/companyMode', providerParams);

const formatLicenseGoal = (license: License | null) => {
  if(!license || !license.licenseGoal) return '-';
  return license.licenseGoal.licenseGoalStatements?.map((statement) => {
    if(statement.type === 'SumOfDistance') {
      if(!statement.data?.target) return '-'
      return `${(statement.data.target/1000).toFixed(2)} km`
    } else if(statement.type === 'DaysWithTour') {
      if(!statement.data?.target) return '-'
      return `${statement.data.target} Tage`
    } else {
      return null
    }
  })?.join(', ');
};

const formatLicenseReward = (license: License | null) => {
  if(!license || !license.licenseReward) return '-';
  return license.licenseReward.licenseBounties?.map((bounty) => {
    if(bounty.rewardType === 'chf') {
      if(!bounty.amount) return '-'
      return `${bounty.amount} CHF`
    } else if(bounty.rewardType === 'holiday') {
      if(!bounty.amount) return '-'
      return `${bounty.amount} Urlaubstage`
    } else if(bounty.rewardType === 'voucher') {
      if(!bounty.amount) return '-'
      return `${bounty.amount} CHF Gutschein`
    } else {
      return null
    }
  })?.join(', ');
};

const records = computed(() => unref(items)?.['hydra:member']);
const total = computed(() => unref(items)?.['hydra:totalItems']);
const filters = computed(() =>
  columns.flatMap((col): Filter[] => {
    if (!col.filter) return [];
    if (Array.isArray(col.filter))
      return col.filter.map((f) => ({
        key: col.key,
        ...f,
      }));
    return [{ key: col.key, ...col.filter }];
  })
);
</script>

<style lang="scss" scoped>
button {
  @apply block;
}

.table {
  @apply flex flex-col;
}
</style>
